/**
 * TEMP — Android voice pipeline diagnostics (on-screen + NDJSON ingest).
 * Remove when Android voice issue is confirmed fixed.
 */
import { ASHLEY_DEBUG_ENABLED } from "./ashleyDebugConfig";

export { ASHLEY_DEBUG_ENABLED };
export const ASHLEY_VOICE_DEBUG = ASHLEY_DEBUG_ENABLED;

/** TEMP — skip speakAshley(); go straight to proceedToListening(). Active only when debug is on. */
export const ASHLEY_BYPASS_TTS_DIAGNOSTIC = ASHLEY_DEBUG_ENABLED;

const MAX_STEPS = 40;
/** @type {Array<{timestamp: number, message: string, hypothesisId?: string, location?: string, data?: object}>} */
const steps = [];
/** @type {Set<(steps: typeof steps) => void>} */
const listeners = new Set();

const INGEST_URL =
  "http://127.0.0.1:7939/ingest/7351eb42-ca54-4693-97ac-948245c2a42c";
const SESSION_ID = "90cce2";

/** @type {number | null} */
let lastGestureTimestamp = null;

export function markAshleyUserGesture(source = "unknown") {
  lastGestureTimestamp = Date.now();
  voiceDiag(`User gesture marked (${source})`, {
    hypothesisId: "H-A",
    location: "ashleyVoiceDiagnostics.markAshleyUserGesture",
    data: { source },
  });
}

export function msSinceLastGesture() {
  if (lastGestureTimestamp == null) {
    return null;
  }
  return Date.now() - lastGestureTimestamp;
}

/**
 * @param {string} message Human-readable status for on-screen panel
 * @param {{ hypothesisId?: string, location?: string, data?: object }} [options]
 */
export function voiceDiag(message, options = {}) {
  if (!ASHLEY_VOICE_DEBUG) {
    return;
  }

  const { hypothesisId, location = "ashleyVoiceDiagnostics", data } = options;
  const entry = {
    timestamp: Date.now(),
    message,
    hypothesisId,
    location,
    data: data ?? undefined,
  };

  steps.push(entry);
  if (steps.length > MAX_STEPS) {
    steps.shift();
  }

  listeners.forEach((listener) => listener([...steps]));
  console.log("[AshleyDiag]", message, data ?? "");

  // #region agent log
  fetch(INGEST_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Debug-Session-Id": SESSION_ID,
    },
    body: JSON.stringify({
      sessionId: SESSION_ID,
      location,
      hypothesisId,
      message,
      data,
      timestamp: entry.timestamp,
    }),
  }).catch(() => {});
  // #endregion
}

/** @param {(steps: typeof steps) => void} listener */
export function subscribeVoiceDiag(listener) {
  listeners.add(listener);
  listener([...steps]);
  return () => listeners.delete(listener);
}

export function getSpeechRecognitionAvailability() {
  if (typeof window === "undefined") {
    return { available: false, api: "none" };
  }

  if ("SpeechRecognition" in window) {
    return { available: true, api: "SpeechRecognition" };
  }

  if ("webkitSpeechRecognition" in window) {
    return { available: true, api: "webkitSpeechRecognition" };
  }

  return { available: false, api: "none" };
}

export async function captureEnvironmentSnapshot() {
  const speechRec = getSpeechRecognitionAvailability();
  const snapshot = {
    isSecureContext:
      typeof window !== "undefined" && Boolean(window.isSecureContext),
    protocol: typeof window !== "undefined" ? window.location?.protocol : null,
    hostname: typeof window !== "undefined" ? window.location?.hostname : null,
    displayMode:
      typeof window !== "undefined" &&
      window.matchMedia("(display-mode: standalone)").matches
        ? "standalone-pwa"
        : "browser",
    speechRecognition: speechRec,
    speechSynthesis:
      typeof window !== "undefined" && "speechSynthesis" in window,
    userAgent:
      typeof navigator !== "undefined"
        ? navigator.userAgent?.slice(0, 140)
        : null,
    msSinceGesture: msSinceLastGesture(),
  };

  if (typeof navigator !== "undefined" && navigator.permissions?.query) {
    try {
      const micPerm = await navigator.permissions.query({ name: "microphone" });
      snapshot.micPermission = micPerm.state;
    } catch (error) {
      snapshot.micPermission = `query-failed: ${error?.message || String(error)}`;
    }
  } else {
    snapshot.micPermission = "permissions-api-unavailable";
  }

  voiceDiag(
    speechRec.available
      ? `SpeechRecognition available (${speechRec.api})`
      : "SpeechRecognition unavailable",
    {
      hypothesisId: "H-E",
      location: "ashleyVoiceDiagnostics.captureEnvironmentSnapshot",
      data: snapshot,
    },
  );

  if (!snapshot.isSecureContext) {
    voiceDiag("Not a secure context (HTTPS required for mic)", {
      hypothesisId: "H-E",
      location: "ashleyVoiceDiagnostics.captureEnvironmentSnapshot",
      data: { protocol: snapshot.protocol },
    });
  }

  if (snapshot.micPermission === "denied") {
    voiceDiag(
      "Mic permission denied — enable in Chrome site settings",
      {
        hypothesisId: "H-F",
        location: "ashleyVoiceDiagnostics.captureEnvironmentSnapshot",
      },
    );
  } else if (snapshot.micPermission === "granted") {
    voiceDiag("Mic permission granted", {
      hypothesisId: "H-F",
      location: "ashleyVoiceDiagnostics.captureEnvironmentSnapshot",
    });
  } else {
    voiceDiag(`Mic permission state: ${snapshot.micPermission}`, {
      hypothesisId: "H-F",
      location: "ashleyVoiceDiagnostics.captureEnvironmentSnapshot",
    });
  }

  return snapshot;
}
